import { Flex, Stack, Text, Image, HStack, Grid, Icon } from "@chakra-ui/react";
import { FaCircleArrowRight } from "react-icons/fa6";
import { RiShieldUserLine } from "react-icons/ri";
import { VscWorkspaceTrusted } from "react-icons/vsc";
import { GrUserExpert } from "react-icons/gr";
import { GiCheckMark } from "react-icons/gi";

import { FilledButton, SimpleOutlineButton } from "../components/custom/Button";
import AppointButton from "../components/custom/AppointButton";
import CustomCard from "../components/custom/CustomCard";
import TestimonialSwiper from "../components/custom/TestimonialSwiper";
import RichText from "../components/custom/RichText";
import { useColorModeValue } from "../components/ui/color-mode";
import { Helmet } from "react-helmet-async";

const ITRFilingScreen = () => {
  const secondBg = useColorModeValue("#b8fff7", "#090110");

  const itrfeatures = [
    "Effortless ITR filing",
    "Maximum Tax Savings",
    "Prompt Processing and Confirmation",
    "Enhanced Security Measures",
    "Expert Support at Your Fingertips",
  ];

  const itrcards = [
    {
      src: "../assets/itr/eligibility.png",
      title: "Eligibility",
      description:
        "Taxpayers who miss the deadline for filing a return by 31st July can opt to file a belated return by December 31 of the relevant assessment year.",
    },
    {
      src: "../assets/itr/deadline.png",
      title: "Deadline",
      description:
        "You can file a belated return on or before 31st December of the relevant assessment year.",
    },
    {
      src: "../assets/itr/refund.png",
      title: "Impact on Refunds",
      description:
        "You can still claim a refund when filing a belated return under Section 139(4). Ensure your bank account is pre-validated on the e-filing portal for direct credit of any refund due.",
    },
    {
      src: "../assets/itr/penalty.png",
      title: "Penalties & Interest",
      description:
        "A late fee under Section 234F applies: ₹5,000 for income above ₹5 lakhs and ₹1,000 for income below ₹5 lakhs. No penalty if income is below the basic exemption limit. Additionally, 1% interest per month on unpaid tax applies under Section 234A.",
    },
  ];

  const stats = [
    {
      icon: RiShieldUserLine,
      stat: "500+",
      label: "ITR Filed",
    },
    {
      icon: VscWorkspaceTrusted,
      stat: "95% ",
      label: "Client Satisfaction",
    },
    {
      icon: GrUserExpert,
      stat: "5+",
      label: "Years of Experience",
    },
  ];
  return (
    <>
      {/* Helmet */}
      <Helmet>
        <title>Income Tax Return Filing - 360fintax</title>
        <meta
          name="description"
          content="File your Income Tax Returns effortlessly with 360fintax. Our experts ensure accurate and timely filing, maximizing your eligible deductions."
        />
        <link
          rel="canonical"
          href="https://tax-services-web.vercel.app/itr-filing"
        />
      </Helmet>

      {/* Hero Section */}
      <Stack
        bgColor={secondBg}
        py="10"
        px={{ base: "5", md: "10", lg: "20" }}
        alignItems="center"
      >
        <Flex
          alignItems="center"
          justifyContent="center"
          direction={{ base: "column-reverse", md: "row" }}
          gap={{ base: "5", md: "10", lg: "40" }}
          py="10"
        >
          <Stack
            gap={5}
            justifyContent="start"
            alignItems={{ base: "center", md: "start" }}
          >
            <Text
              fontSize={{ base: "2xl", md: "3xl", lg: "4xl" }}
              fontWeight="semibold"
              textAlign={{ base: "center", md: "left" }}
            >
              Looking for a dedicated CA to handle your taxes?
            </Text>
            <Flex
              gap={5}
              fontSize={{ base: "2xs", md: "2xs", lg: "sm" }}
              display={{ base: "none", md: "flex" }}
            >
              <HStack>
                <FaCircleArrowRight />
                <Text>All-Round Support</Text>
              </HStack>
              <HStack>
                <FaCircleArrowRight />
                <Text>Reliable and Secure</Text>
              </HStack>
              <HStack>
                <FaCircleArrowRight />
                <Text>Lowest Filing Fees</Text>
              </HStack>
            </Flex>
            <Flex>
              <AppointButton
                text={<SimpleOutlineButton title="Book a Call Now" />}
              />
            </Flex>
          </Stack>
          <Image
            src="../assets/itr/itrhero.png"
            h={{ base: "300px", md: "300px", lg: "350px" }}
            w={{ base: "300px", md: "300px", lg: "350px" }}
            loading="eager"
          />
        </Flex>
        <Flex
          gap={{ base: "5", md: "10", lg: "20" }}
          direction={{ base: "column", md: "row" }}
          w="full"
          justifyContent="center"
        >
          {stats.map((stat, index) => (
            <HStack>
              <Icon boxSize={{ base: "8", md: "6" }} color="#008080">
                <stat.icon />
              </Icon>
              <Text fontSize={{ base: "xl", md: "md" }}>
                <span style={{ fontWeight: "bold" }}>{stat.stat}</span>{" "}
                {stat.label}
              </Text>
            </HStack>
          ))}
        </Flex>
      </Stack>

      <Stack alignItems="center" px={{ base: "5", md: "10", lg: "20" }} py={10}>
        <RichText
          subtitle="Income Tax Return Filing"
          title="Streamline Your Tax Compliance"
        />
        <Grid
          templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }}
          py={2}
          alignItems="center"
          justifyItems="center"
          gap={{ base: "10", md: "0" }}
        >
          <Stack gap={5}>
            {itrfeatures.map((feature, index) => (
              <HStack gap={3}>
                <GiCheckMark />
                <Text fontSize="lg">{feature}</Text>
              </HStack>
            ))}
            <FilledButton
              title="File Your ITR Now"
              mx={{ base: "auto", md: "0" }}
              url="/itr-form"
            />
          </Stack>
          <Image
            src="../assets/itr/itrlandscape.png"
            w="full"
            objectFit="contain"
            data-aos="zoom-in"
          />
        </Grid>
      </Stack>

      <Stack
        bgColor={secondBg}
        alignItems="center"
        gap={10}
        py={10}
        data-aos="fade-up"
      >
        <RichText
          title="Missed the ITR Deadline? Here's How to File a Belated Return"
          colorText="ITR Deadline?"
        />
        <Grid
          templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }}
          px={{ base: "5", md: "10", lg: "10" }}
          gap={{ base: "10", md: "10", lg: "10" }}
          justifyItems="center"
        >
          {itrcards.map(({ src, title, description }) => (
            <CustomCard
              title={title}
              description={description}
              src={src}
              data-aos="flip-right"
            />
          ))}
        </Grid>
      </Stack>

      {/* Testmonials */}
      <Stack
        data-aos="fade-up"
        textAlign="center"
        gap="10"
        py={{ base: "10", md: "10", lg: "20" }}
        alignItems="center"
      >
        <RichText
          title="Trusted by Clients,Proven Results"
          colorText="Proven Results"
          description="Discover how we've helped individuals and businesses stay compliant and stress-free, year after year."
          w={{ base: "xs", md: "3xl" }}
        />

        <TestimonialSwiper />
      </Stack>
    </>
  );
};

export default ITRFilingScreen;
